import { __awaiter } from "tslib";
import React from 'react';
import { Syringe } from 'mana-syringe';
import { Deferred } from '../../common/deferred';
import { MAX_ZOOM, MIN_ZOOM, XFLOW_DEFAULT_EDGE, XFLOW_DEFAULT_NODE, XFLOW_DEFAULT_GROUP_NODE, } from '../../constants';
import { uuidv4 } from '../../common/uuid';
import { XFlowDefaultGroupNode } from './react-group-node';
import { XFlowDefaultNode } from './react-node';
export var NsGraphConfig;
(function (NsGraphConfig) {
    NsGraphConfig.CONFIG_TYPE = 'GraphConfig';
    /** 默认的Node解析函数 */
    NsGraphConfig.defaultNodeTypeParser = (nodeConfig) => nodeConfig.renderKey;
    /** 构造的Edge解析函数 */
    NsGraphConfig.defaultEdgeTypeParser = (edgeConfig) => edgeConfig.renderKey;
    /** XFlow默认的React组件 */
    NsGraphConfig.defaultNodeMapValue = [
        [XFLOW_DEFAULT_NODE, XFlowDefaultNode],
        [XFLOW_DEFAULT_GROUP_NODE, XFlowDefaultGroupNode],
    ];
})(NsGraphConfig || (NsGraphConfig = {}));
export class GraphConfig {
    /** 构造函数 */
    constructor() {
        /** 配置类型 */
        this.CONFIG_TYPE = NsGraphConfig.CONFIG_TYPE;
        /** 画布的属性 */
        this.x6Options = new Deferred();
        /** 画布的root节点 */
        this.rootContainer = new Deferred();
        /** 画布的dom节点 */
        this.graphContainer = new Deferred();
        /** 自定义节点 */
        this.nodeRender = new Map(NsGraphConfig.defaultNodeMapValue);
        /** 自定义边 */
        this.edgeRender = new Map();
        /** 解析node渲染类型的parser */
        this.nodeTypeParser = NsGraphConfig.defaultNodeTypeParser;
        /** 解析edge渲染类型的parser  */
        this.edgeTypeParser = NsGraphConfig.defaultEdgeTypeParser;
        /** 自定义事件 */
        this.events = [];
        this.mergeX6Config = (config) => __awaiter(this, void 0, void 0, function* () {
            const current = yield this.getX6Config();
            return Object.assign(current, config);
        });
        this.getX6Config = () => __awaiter(this, void 0, void 0, function* () {
            const config = yield this.x6Options.promise;
            return config;
        });
        this.setX6Config = (options) => {
            var _a;
            const defaultOptions = this.getDefaultGraphOptions();
            (_a = this.x6Options) === null || _a === void 0 ? void 0 : _a.resolve(Object.assign(Object.assign({}, defaultOptions), options));
        };
        this.setRootContainer = (ele) => {
            ele && (this === null || this === void 0 ? void 0 : this.rootContainer.resolve(ele));
        };
        this.setGraphContainer = (ele) => {
            var _a;
            ele && ((_a = this.graphContainer) === null || _a === void 0 ? void 0 : _a.resolve(ele));
        };
        this.setEvents = (events) => {
            this.events = events;
        };
        this.setDefaultNodeRender = (component) => {
            this.nodeRender.set(XFLOW_DEFAULT_NODE, component);
        };
        this.setDefaultEdgeRender = (component) => {
            this.edgeRender.set(XFLOW_DEFAULT_EDGE, component);
        };
        this.setNodeRender = (renderKey, component) => {
            this.nodeRender.set(renderKey, component);
        };
        this.setEdgeRender = (renderKey, component) => {
            this.edgeRender.set(renderKey, component);
        };
        /** 设置解析node render key的 parser */
        this.setNodeTypeParser = (parser) => {
            this.nodeTypeParser = parser;
        };
        /** 设置解析edge render key的 parser */
        this.setEdgeTypeParser = (parser) => {
            this.edgeTypeParser = parser;
        };
        /** 获取Graph参数 */
        this.getConfig = () => __awaiter(this, void 0, void 0, function* () {
            const [rootContainer, graphContainer, x6Options] = yield Promise.all([
                this.rootContainer.promise,
                this.graphContainer.promise,
                this.x6Options.promise,
            ]);
            const graphConfig = {
                xflowInstanceId: this.xflowInstanceId,
                graphId: this.graphId,
                nodeViewId: this.graphId,
                rootContainer,
                graphContainer,
                x6Options,
                events: this.events,
                nodeRender: this.nodeRender,
                edgeRender: this.edgeRender,
                nodeTypeParser: this.nodeTypeParser,
                edgeTypeParser: this.edgeTypeParser,
            };
            return graphConfig;
        });
        this.getDefaultGraphOptions = () => {
            const defaultOptions = {
                // 节点是否可旋转
                rotating: false,
                // 节点是否可调整大小
                resizing: false,
                // 节点连线规则配置（详细文档：https://X6.antv.vision/zh/docs/api/graph/interaction#connecting）
                connecting: {
                    snap: true,
                    dangling: false,
                    highlight: false,
                    connectionPoint: 'rect',
                    router: { name: 'er' },
                    connector: {
                        name: 'rounded',
                        args: {
                            radius: 15,
                        },
                    },
                },
                // 画布背景，支持颜色/图片/水印等（详细文档：https://X6.antv.vision/zh/docs/tutorial/basic/background）
                background: {},
                // 网格配置（详细文档：https://X6.antv.vision/zh/docs/tutorial/basic/grid）
                grid: {
                    visible: true,
                },
                // 点选/框选配置（详细文档：https://X6.antv.vision/zh/docs/tutorial/basic/selection）
                selecting: {
                    strict: true,
                    enabled: true,
                    multiple: true,
                    selectCellOnMoved: true,
                    showNodeSelectionBox: false,
                    // 框选可以选中edge
                    rubberEdge: true,
                    // 框选可以选中node
                    rubberNode: true,
                    movable: true,
                },
                // 对齐线配置，辅助移动节点排版（详细文档：https://X6.antv.vision/zh/docs/tutorial/basic/snapline）
                snapline: {
                    enabled: false,
                },
                // 撤销/重做能力（详细文档：https://X6.antv.vision/zh/docs/tutorial/basic/history）
                history: {
                    enabled: false,
                },
                // 剪切板，支持跨画布的复制/粘贴（详细文档：https://X6.antv.vision/zh/docs/tutorial/basic/clipboard）
                clipboard: {
                    enabled: true,
                    useLocalStorage: true,
                },
                // 使画布具备滚动、平移、居中、缩放等能力（详细文档：https://X6.antv.vision/zh/docs/tutorial/basic/scroller）
                scroller: {
                    enabled: false,
                },
                // 滚轮缩放 （详细文档：https://X6.antv.vision/zh/docs/tutorial/basic/mousewheel）
                mousewheel: {
                    enabled: true,
                    minScale: MIN_ZOOM,
                    maxScale: MAX_ZOOM,
                    factor: 1.1,
                    modifiers: ['ctrl', 'meta'],
                },
                /** 普通画布, 支持拖拽平移 */
                panning: {
                    enabled: true,
                },
                /** 缩放参数 */
                scaling: {
                    min: MIN_ZOOM,
                    max: MAX_ZOOM,
                },
                /** keyboad */
                keyboard: {
                    enabled: true,
                },
                /** 定制节点和边的交互行为 */
                interacting: {
                    /** 节点默认可以被移动 */
                    nodeMovable: true,
                    /** 边上标签默认不可以被移动 */
                    edgeLabelMovable: false,
                },
                async: false,
            };
            return defaultOptions;
        };
        this.dispose = () => {
            // TODO: 统一看下defer的销毁
            this.rootContainer = new Deferred();
            this.graphContainer = new Deferred();
            this.x6Options = new Deferred();
        };
        this.graphId = uuidv4();
    }
}
export const IGraphOptionProvider = Symbol('IGraphOptionProvider');
export const registerGraphConfig = (register, graphConfig) => {
    register(IGraphOptionProvider, {
        lifecycle: Syringe.Lifecycle.singleton,
        useDynamic: () => {
            return {
                getOptions: () => __awaiter(void 0, void 0, void 0, function* () {
                    return graphConfig.getConfig();
                }),
            };
        },
    });
};
export const createGraphConfig = (addOptions) => (props) => {
    /** bridge config and props */
    const propsContainer = React.useMemo(() => ({ getValue: () => ({}) }), []);
    propsContainer.getValue = () => props;
    const graphConfig = React.useMemo(() => {
        const config = new GraphConfig();
        addOptions(config, propsContainer);
        return config;
    }, [propsContainer]);
    return graphConfig;
};
//# sourceMappingURL=config.js.map